// ============================================================
// Cloud store — Firestore + Firebase Storage med realtidssynk.
// Firestore-struktur:
//   users/{uid}/companies/{companyId}/years/{year}
//     - skattekontoSaldo, skattekontoUpdated
//     - villkoratTillskott, villkoratTillskottUpdated
//     - months[12]: { vatIn, vatOut, income, expense }
//   users/{uid}/companies/{companyId}/receipts/{receiptId}
//     - filename, date, category, amount, currency, storagePath, createdAt
//
// Storage-struktur:
//   users/{uid}/{companyId}/{year}/{category}/{id}-{filename}
// ============================================================

// TODO: add retry/backoff for transient Firestore errors
import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  deleteDoc,
  getDocs,
  runTransaction,
  type DocumentData,
} from 'firebase/firestore'
import {
  ref as storageRef,
  uploadBytes,
  deleteObject,
  getDownloadURL,
} from 'firebase/storage'
import { db, storage, auth } from '../firebase'
import type { ReceiptCategory } from './receipts'
import { parseFilename } from './receipts'
import { emptyMonths, type MonthlyRow } from '../data/store'

// --- Retry helper (exponential backoff) ---

async function withRetry<T>(
  fn: () => Promise<T>,
  maxAttempts = 4,
  baseDelayMs = 1000,
): Promise<T> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (err: any) {
      const isRetryable =
        err?.code === 'storage/retry-limit-exceeded' ||
        err?.code === 'storage/unknown' ||
        err?.message?.includes('retry') ||
        err?.message?.includes('network')
      if (!isRetryable || attempt === maxAttempts) throw err
      const delay = baseDelayMs * Math.pow(2, attempt - 1) + Math.random() * 500
      await new Promise((r) => setTimeout(r, delay))
    }
  }
  throw new Error('withRetry: unreachable')
}

// --- Types ---

/** Senast-syncad-status per kategori (kundfakturor/leverantorsfakturor/utlagg) */
export interface CategorySyncStatus {
  lastSyncAt: string         // ISO timestamp
  fileCount: number          // antal filer i denna batch
  source: 'manual-upload' | 'dropbox-scanner' | 'sie-import'
}

export interface CloudYearDoc {
  year: number
  skattekontoSaldo?: number
  skattekontoUpdated?: string
  /** Villkorat aktieägartillskott (ej återbetalat) per balansdag / löpande */
  villkoratTillskott?: number
  villkoratTillskottUpdated?: string
  dupScanDate?: string
  /** Senast-syncad per kategori — visas i Import-vyn och i bolag-vaeljaren */
  syncStatus?: {
    kundfakturor?: CategorySyncStatus
    leverantorsfakturor?: CategorySyncStatus
    utlagg?: CategorySyncStatus
  }
  months: MonthlyRow[]
}

/** Kvalitetsnivå — hur pålitlig är kvittodatan?
 *  red      = AI misslyckades eller inga fält alls
 *  orange   = AI fyllde något men datum/belopp saknas
 *  yellow   = datum + belopp finns men titel/valuta saknas
 *  green    = alla fält ifyllda (av AI)
 *  verified = manuellt kontrollerad av användare
 */
export type ReceiptQuality = 'red' | 'orange' | 'yellow' | 'green' | 'verified'

/** Hur härstammar moms-fältet på en post?
 *  ai_extracted = AI-OCR fyllde i vatAmount/vatRate
 *  manual       = användare matade in manuellt
 *  sie_import   = kom från SIE-fil
 *  missing      = saknas (visas som 0 kr moms — INGEN tyst default)
 *
 *  Bakåtkompatibilitet: poster utan vatStatus behandlas som 'missing'
 *  om vatAmount == null.
 */
export type VatStatus = 'ai_extracted' | 'manual' | 'sie_import' | 'missing'

export interface CloudReceipt {
  id: string
  companyId: string
  year: number
  month: number
  category: ReceiptCategory
  filename: string
  date?: string
  title?: string
  amount?: number          // totalbelopp inkl moms
  amountExVat?: number     // belopp exkl moms
  vatAmount?: number       // momsbelopp
  vatRate?: number         // momssats som decimal (0.25 = 25%)
  currency?: string
  accountCode?: number     // BAS-kontonummer (t.ex. 5410, 3010, 6110)
  accountLabel?: string    // kontobeskrivning (t.ex. "Förbrukningsinventarier")
  storagePath: string
  downloadUrl?: string
  sourcePath?: string
  source?: string
  size: number
  mime: string
  createdAt: string
  fileHash?: string
  quality?: ReceiptQuality
  vatStatus?: VatStatus    // FAS A (2026-05-21): hur härstammar moms-fältet
}

/** Beräkna kvalitetsnivå utifrån vilka fält som är ifyllda */
export function computeQuality(r: Pick<CloudReceipt, 'date' | 'title' | 'amount' | 'currency' | 'vatRate' | 'accountCode' | 'quality'>): ReceiptQuality {
  // Manuellt verifierad trumpar allt
  if (r.quality === 'verified') return 'verified'
  const hasDate = !!r.date
  const hasAmount = r.amount != null
  const hasTitle = !!r.title
  const hasCurrency = !!r.currency
  const hasVat = r.vatRate != null
  const hasAccount = r.accountCode != null
  // Grön = alla bokföringsfält ifyllda (datum, belopp, moms, konto)
  if (hasDate && hasAmount && hasTitle && hasCurrency && hasVat && hasAccount) return 'green'
  // Gul = datum + belopp + moms OK
  if (hasDate && hasAmount && hasVat) return 'yellow'
  // Orange = grundläggande data finns
  if (hasDate && hasAmount) return 'orange'
  if (hasDate || hasAmount || hasTitle) return 'orange'
  return 'red'
}

export interface DuplicateMatch {
  receipt: CloudReceipt
  reason: 'exact-hash' | 'fuzzy-amount-date' | 'same-filename'
}

// --- Helpers ---

function requireUid(): string {
  const uid = auth.currentUser?.uid
  if (!uid) throw new Error('Inte inloggad')
  return uid
}

function yearDocRef(uid: string, companyId: string, year: number) {
  return doc(db, 'users', uid, 'companies', companyId, 'years', String(year))
}

function receiptsCol(uid: string, companyId: string) {
  return collection(db, 'users', uid, 'companies', companyId, 'receipts')
}

// --- Logs (client-side errors → Firestore) ---

export type LogLevel = 'info' | 'warn' | 'error'

export interface LogEntry {
  id: string
  level: LogLevel
  source: string         // t.ex. 'aiAnalyzeReceipt', 'uploadPdf', 'storage-fetch'
  message: string
  context?: Record<string, unknown>   // godtycklig metadata: receiptId, storagePath, status
  createdAt: string      // ISO timestamp (serverTimestamp via Firestore om null)
  companyId?: string     // valfri — vilket bolag detta gäller
}

/**
 * Skriv en logg-rad till Firestore på `users/{uid}/logs/{auto}`.
 * Best-effort: kastar inte vid fel (vill inte att loggning ska bryta huvudflödet).
 *
 * **Användning:**
 *   await writeLog('error', 'aiAnalyzeReceipt', 'Storage proxy 403', {
 *     receiptId: r.id, storagePath: r.storagePath, status: 403
 *   })
 *
 * **Retention:** Inga TTL-fält ännu — använd `purgeOldLogs()` manuellt
 * eller bygg en Cloud Function senare.
 */
export async function writeLog(
  level: LogLevel,
  source: string,
  message: string,
  context?: Record<string, unknown>,
  companyId?: string,
): Promise<void> {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) return  // tyst — loggning utan inloggning är ändå värdelös
    await addDoc(collection(db, 'users', uid, 'logs'), {
      level,
      source,
      message: message.slice(0, 2000),  // hard cap så Firestore-doc inte spränger
      context: context ?? null,
      companyId: companyId ?? null,
      createdAt: serverTimestamp(),
      createdAtIso: new Date().toISOString(),  // direkt-läsbar i UI utan att vänta på server
    })
  } catch (e) {
    // Best-effort — om loggning failar, t.ex. offline, är det inte fatalt
    console.warn('[cloudStore] writeLog: failed to write log entry to Firestore (best-effort, ignoring):', e)
  }
}

/**
 * Prenumerera på senaste N logg-rader (default 200, sorterat nyast först).
 * Returnerar en unsubscribe-funktion.
 */
export function subscribeLogs(
  cb: (entries: LogEntry[]) => void,
  limitCount = 200,
): () => void {
  const uid = requireUid()
  const colRef = collection(db, 'users', uid, 'logs')
  const q = query(colRef, orderBy('createdAtIso', 'desc'))
  return onSnapshot(q, (snap) => {
    const entries: LogEntry[] = []
    let n = 0
    for (const d of snap.docs) {
      if (n >= limitCount) break
      const data = d.data() as DocumentData
      entries.push({
        id: d.id,
        level: (data.level as LogLevel) ?? 'info',
        source: (data.source as string) ?? 'unknown',
        message: (data.message as string) ?? '',
        context: (data.context as Record<string, unknown>) ?? undefined,
        createdAt: (data.createdAtIso as string) ?? new Date().toISOString(),
        companyId: (data.companyId as string) ?? undefined,
      })
      n++
    }
    cb(entries)
  })
}

/**
 * Radera alla logg-rader för inloggad användare (manuell rensning).
 */
export async function clearAllLogs(): Promise<number> {
  const uid = requireUid()
  const colRef = collection(db, 'users', uid, 'logs')
  const snap = await getDocs(colRef)
  let count = 0
  for (const d of snap.docs) {
    await deleteDoc(d.ref)
    count++
  }
  return count
}

// --- Year-dokument ---

export function subscribeYear(
  companyId: string,
  year: number,
  onChange: (data: CloudYearDoc) => void
): () => void {
  const uid = requireUid()
  return onSnapshot(yearDocRef(uid, companyId, year), (snap) => {
    if (!snap.exists()) {
      onChange({ year, months: emptyMonths() })
      return
    }
    const data = snap.data() as DocumentData
    onChange({
      year: data.year ?? year,
      skattekontoSaldo: data.skattekontoSaldo,
      skattekontoUpdated: data.skattekontoUpdated,
      villkoratTillskott: data.villkoratTillskott,
      villkoratTillskottUpdated: data.villkoratTillskottUpdated,
      dupScanDate: data.dupScanDate,
      syncStatus: data.syncStatus,
      months: Array.isArray(data.months) && data.months.length === 12
        ? (data.months as MonthlyRow[])
        : emptyMonths(),
    })
  })
}

export async function setSkattekontoCloud(
  companyId: string,
  year: number,
  saldo: number
) {
  const uid = requireUid()
  await setDoc(
    yearDocRef(uid, companyId, year),
    {
      year,
      skattekontoSaldo: saldo,
      skattekontoUpdated: new Date().toISOString(),
    },
    { merge: true }
  )
}

export async function setVillkoratTillskottCloud(
  companyId: string,
  year: number,
  belopp: number
) {
  const uid = requireUid()
  await setDoc(
    yearDocRef(uid, companyId, year),
    {
      year,
      villkoratTillskott: belopp,
      villkoratTillskottUpdated: new Date().toISOString(),
    },
    { merge: true }
  )
}

export async function applyMonthsCloud(
  companyId: string,
  year: number,
  months: MonthlyRow[],
  merge = false
) {
  const uid = requireUid()
  const ref = yearDocRef(uid, companyId, year)
  if (!merge) {
    await setDoc(ref, { year, months }, { merge: true })
    return
  }
  await runTransaction(db, async (tx) => {
    const snapDoc = await tx.get(ref)
    const existing: MonthlyRow[] =
      snapDoc.exists() && Array.isArray(snapDoc.data().months) && snapDoc.data().months.length === 12
        ? (snapDoc.data().months as MonthlyRow[])
        : emptyMonths()
    const next = existing.map((e, i) => ({
      month: i,
      vatIn: e.vatIn + (months[i]?.vatIn ?? 0),
      vatOut: e.vatOut + (months[i]?.vatOut ?? 0),
      income: e.income + (months[i]?.income ?? 0),
      expense: e.expense + (months[i]?.expense ?? 0),
    }))
    tx.set(ref, { year, months: next }, { merge: true })
  })
}

// --- Kvitton (Storage + Firestore) ---

export function subscribeReceipts(
  companyId: string,
  year: number,
  onChange: (list: CloudReceipt[]) => void
): () => void {
  const uid = requireUid()
  const q = query(
    receiptsCol(uid, companyId),
    where('year', '==', year),
    orderBy('createdAt', 'desc')
  )
  return onSnapshot(q, (snap) => {
    const list: CloudReceipt[] = []
    snap.forEach((d) => {
      const x = d.data() as DocumentData
      list.push({
        id: d.id,
        companyId,
        year: x.year,
        month: x.month,
        category: x.category,
        filename: x.filename,
        date: x.date,
        title: x.title,
        amount: x.amount,
        amountExVat: x.amountExVat,
        vatAmount: x.vatAmount,
        vatRate: x.vatRate,
        currency: x.currency,
        accountCode: x.accountCode,
        accountLabel: x.accountLabel,
        storagePath: x.storagePath,
        downloadUrl: x.downloadUrl,
        size: x.size,
        mime: x.mime,
        createdAt: x.createdAt,
        fileHash: x.fileHash,
        quality: x.quality,
        vatStatus: x.vatStatus,
      })
    })
    onChange(list)
  })
}

/** Subscribe to ALL receipts for a company (no year filter) — for overview/stats */
export function subscribeAllReceipts(
  companyId: string,
  onChange: (list: CloudReceipt[]) => void
): () => void {
  const uid = requireUid()
  const q = query(
    receiptsCol(uid, companyId),
    orderBy('createdAt', 'desc')
  )
  return onSnapshot(q, (snap) => {
    const list: CloudReceipt[] = []
    snap.forEach((d) => {
      const x = d.data() as DocumentData
      list.push({
        id: d.id,
        companyId,
        year: x.year,
        month: x.month,
        category: x.category,
        filename: x.filename,
        date: x.date,
        title: x.title,
        amount: x.amount,
        amountExVat: x.amountExVat,
        vatAmount: x.vatAmount,
        vatRate: x.vatRate,
        currency: x.currency,
        accountCode: x.accountCode,
        accountLabel: x.accountLabel,
        storagePath: x.storagePath,
        downloadUrl: x.downloadUrl,
        size: x.size,
        mime: x.mime,
        createdAt: x.createdAt,
        fileHash: x.fileHash,
        quality: x.quality,
        vatStatus: x.vatStatus,
      })
    })
    onChange(list)
  })
}

// --- Duplicate detection ---

async function hashFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const hash = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// TODO: cache receipt list for batch uploads
export async function checkDuplicates(
  companyId: string,
  year: number,
  file: File,
  meta: { date?: string; amount?: number; filename: string }
): Promise<DuplicateMatch[]> {
  const uid = requireUid()
  const matches: DuplicateMatch[] = []
  const fileHash = await hashFile(file)

  const colRef = collection(db, 'users/' + uid + '/companies/' + companyId + '/receipts')
  const q = query(colRef, where('year', '==', year))
  const snap = await getDocs(q)
  const existing: CloudReceipt[] = []
  snap.forEach((d) => existing.push({ id: d.id, ...d.data() } as CloudReceipt))

  for (const r of existing) {
    // Exact hash = identical file content — always flag
    if (r.fileHash && r.fileHash === fileHash) {
      matches.push({ receipt: r, reason: 'exact-hash' })
      continue
    }
    // Same filename — flag unless amounts differ significantly (different receipt, same naming pattern)
    if (r.filename === meta.filename) {
      // If both have amounts and they differ by more than 1 kr → different receipt
      if (meta.amount != null && r.amount != null && Math.abs(r.amount - meta.amount) > 1) continue
      matches.push({ receipt: r, reason: 'same-filename' })
      continue
    }
    // Fuzzy amount+date — only if amounts are very close AND dates are close
    // Skip if existing receipt is already verified (user confirmed it's correct)
    if (meta.amount != null && r.amount != null && meta.date && r.date) {
      if (r.quality === 'verified') continue // Verified receipt = user confirmed data, not a duplicate
      const amountClose = Math.abs(r.amount - meta.amount) <= 1 // Tighter: within 1 kr
      const d1 = new Date(meta.date).getTime()
      const d2 = new Date(r.date).getTime()
      const dateClose = !isNaN(d1) && !isNaN(d2) && Math.abs(d1 - d2) <= 86400000 // 1 day (not 2)
      if (amountClose && dateClose) {
        matches.push({ receipt: r, reason: 'fuzzy-amount-date' })
      }
    }
  }

  return matches
}

export async function uploadReceiptCloud(
  companyId: string,
  category: ReceiptCategory,
  file: File,
  fallbackYear: number
): Promise<CloudReceipt> {
  const uid = requireUid()
  const meta = parseFilename(file.name)

  let year = fallbackYear
  let month = new Date().getMonth()
  if (meta.date) {
    const d = new Date(meta.date)
    if (!isNaN(d.getTime())) {
      year = d.getFullYear()
      month = d.getMonth()
    }
  }

  const id = crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)
  const safeName = file.name.replace(/[^\w\s.\-\u00e5\u00e4\u00f6\u00c5\u00c4\u00d6]/g, '_')
  const path = 'users/' + uid + '/' + companyId + '/' + year + '/' + category + '/' + id + '-' + safeName

  const sRef = storageRef(storage, path)
  await withRetry(() => uploadBytes(sRef, file, { contentType: file.type || 'application/pdf' }))
  // TODO: public download URLs expose files — consider signed URLs or proxy
  const downloadUrl = await getDownloadURL(sRef)

  const fHash = await hashFile(file)

  const rec: Omit<CloudReceipt, 'id'> = {
    companyId,
    year,
    month,
    category,
    filename: file.name,
    date: meta.date,
    title: meta.title,
    amount: meta.amount,
    currency: meta.currency,
    storagePath: path,
    downloadUrl,
    size: file.size,
    mime: file.type || 'application/pdf',
    createdAt: new Date().toISOString(),
    fileHash: fHash,
    quality: computeQuality({ date: meta.date, title: undefined, amount: meta.amount, currency: undefined, vatRate: undefined, accountCode: undefined }),
  }

  // Firestore rejects undefined values
  const cleanRec = Object.fromEntries(
    Object.entries(rec).filter(([, v]) => v !== undefined)
  )

  let added;
  try {
    added = await addDoc(receiptsCol(uid, companyId), {
      ...cleanRec,
      createdAtServer: serverTimestamp(),
    })
  } catch (e) {
    // Compensate: remove orphaned Storage blob
    try { await deleteObject(sRef) } catch (cleanupErr) { console.warn('[cloudStore] uploadReceipt: failed to clean up orphan Storage blob after Firestore addDoc error:', cleanupErr) }
    throw e
  }

  return { id: added.id, ...rec }
}

function receiptIsComplete(r: CloudReceipt): boolean {
  return !!(r.date && r.title && r.amount != null && r.currency && r.vatRate != null && r.accountCode != null)
}

/**
 * 2026-05-21 (Re-run AI feature):
 * Ladda upp en PDF/bild till Firebase Storage och uppdatera den befintliga
 * Firestore-postens storagePath/downloadUrl/size/mime + fileHash. Anvands nar
 * en post importerats via JSON-importen (utan binar fil) och vi vill bevara
 * filen permanent sa framtida AI-korningar inte kraver re-upload.
 *
 * Idempotent: om posten redan har storagePath kastas ett fel (anroparen
 * forvantas kontrollera detta innan). Hanterar Firestore-fel genom att
 * radera den orphan-uppladdade filen i Storage.
 */
export async function uploadAndAttachStoragePath(
  companyId: string,
  receipt: CloudReceipt,
  file: File,
): Promise<{ storagePath: string; downloadUrl: string }> {
  if (receipt.storagePath) {
    throw new Error('Post har redan storagePath — använd aiAnalyzeReceipt() direkt')
  }
  const uid = requireUid()
  const id = receipt.id
  const safeName = file.name.replace(/[^\w\s.\-åäöÅÄÖ]/g, '_')
  const path = 'users/' + uid + '/' + companyId + '/' + receipt.year + '/' + receipt.category + '/' + id + '-' + safeName

  const sRef = storageRef(storage, path)
  await withRetry(() => uploadBytes(sRef, file, { contentType: file.type || 'application/pdf' }))
  const downloadUrl = await getDownloadURL(sRef)
  const fHash = await hashFile(file)

  const patch: Record<string, unknown> = {
    storagePath: path,
    downloadUrl,
    size: file.size,
    mime: file.type || 'application/pdf',
    fileHash: fHash,
  }

  try {
    const ref = doc(db, 'users', uid, 'companies', companyId, 'receipts', receipt.id)
    await updateDoc(ref, patch)
  } catch (e) {
    // Kompensera: radera orphan-blob sa Storage inte far en post utan nagon
    // referens i Firestore.
    try { await deleteObject(sRef) } catch (cleanupErr) { console.warn('[cloudStore] uploadAndAttachStoragePath: failed to clean up orphan Storage blob after Firestore updateDoc error:', cleanupErr) }
    throw e
  }

  return { storagePath: path, downloadUrl }
}

// NOTE: storagePath not updated when year changes — known limitation (see updateReceiptFields + EditReceiptModal warning)
export async function aiAnalyzeReceipt(
  companyId: string,
  receipt: CloudReceipt,
  force = false,
  signal?: AbortSignal
): Promise<{ updated: boolean; error?: string; skipped?: boolean }> {
  if (!force && receiptIsComplete(receipt)) {
    return { updated: false, skipped: true, error: 'Alla f\u00e4lt redan ifyllda' }
  }

  const uid = requireUid()
  try {
    // 2026-05-22 alt C: server-side MERGED endpoint /api/storage-extract.
    // Det här endpoint:et hämtar PDF från Storage OCH skickar till xAI i samma
    // anrop. Sparar:
    //   - Nätverk: 1 HTTP-roundtrip (var 2)
    //   - CPU/minne: ingen JSON.parse av base64-strängen i Pages Function
    // Tidigare gjordes det i två steg via /api/storage-fetch + /api/extract,
    // vilket orsakade Worker 502 Bad Gateway på multi-MB PDF:er p.g.a.
    // CPU/minneskostnaden.
    if (!receipt.storagePath) {
      throw new Error('Post saknar storagePath — ingen PDF i Storage att hämta')
    }
    const idToken = await auth.currentUser?.getIdToken()
    if (!idToken) throw new Error('Ingen inloggad användare (saknar id-token)')

    const apiRes = await fetch('/api/storage-extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + idToken,
      },
      body: JSON.stringify({ storagePath: receipt.storagePath }),
      signal,
    })

    // Läs body som text FÖRE JSON.parse så vi inte kraschar på HTML/SPA-fallback
    const ct = apiRes.headers.get('content-type') || ''
    const bodyText = await apiRes.text()
    const snippet = bodyText.replace(/\s+/g, ' ').slice(0, 500)

    if (!ct.includes('json')) {
      const msg = '[STORAGE-EXTRACT v3] /api/storage-extract returnerade icke-JSON (HTTP ' +
        apiRes.status + ', ' + ct + '). Början av svaret: ' + snippet.slice(0, 200)
      void writeLog('error', 'storage-extract', msg, {
        receiptId: receipt.id,
        storagePath: receipt.storagePath,
        filename: receipt.filename,
        httpStatus: apiRes.status,
        contentType: ct,
        responseSnippet: snippet,
      }, companyId)
      throw new Error(msg)
    }

    let res: {
      error?: string; code?: string; raw?: string;
      date?: string; title?: string; amount?: number; amountExVat?: number;
      vatAmount?: number; vatRate?: number; currency?: string;
      accountCode?: number; accountLabel?: string;
    }
    try {
      res = JSON.parse(bodyText)
    } catch (e) {
      const msg = '[STORAGE-EXTRACT v3] Kunde inte parsa JSON-svar (HTTP ' + apiRes.status + '): ' +
        (e as Error).message + ' — body: ' + snippet.slice(0, 200)
      void writeLog('error', 'storage-extract', msg, {
        receiptId: receipt.id,
        storagePath: receipt.storagePath,
        filename: receipt.filename,
        httpStatus: apiRes.status,
        responseSnippet: snippet,
      }, companyId)
      throw new Error(msg)
    }

    if (!apiRes.ok || res.error) {
      const msg = '[STORAGE-EXTRACT v3] ' + (res.code ? '[' + res.code + '] ' : '') +
        (res.error || 'HTTP ' + apiRes.status) +
        ' (path: ' + receipt.storagePath + ')'
      void writeLog('error', 'storage-extract', msg, {
        receiptId: receipt.id,
        storagePath: receipt.storagePath,
        filename: receipt.filename,
        httpStatus: apiRes.status,
        code: res.code,
        detail: res.error,
      }, companyId)
      return { updated: false, error: msg }
    }

    const patch: Record<string, unknown> = {}
    if (res.date && (force || !receipt.date)) patch.date = res.date
    if (res.title && (force || !receipt.title)) patch.title = res.title
    if (res.amount != null && (force || receipt.amount == null)) patch.amount = res.amount
    if (res.amountExVat != null && (force || receipt.amountExVat == null)) patch.amountExVat = res.amountExVat
    if (res.vatAmount != null && (force || receipt.vatAmount == null)) patch.vatAmount = res.vatAmount
    if (res.vatRate != null && (force || receipt.vatRate == null)) patch.vatRate = res.vatRate
    // FAS A (2026-05-21): markera moms-källa när AI fyllt i något moms-fält
    if (patch.vatAmount != null || patch.vatRate != null) patch.vatStatus = 'ai_extracted'
    if (res.currency && (force || !receipt.currency)) patch.currency = res.currency
    if (res.accountCode != null && (force || receipt.accountCode == null)) patch.accountCode = res.accountCode
    if (res.accountLabel && (force || !receipt.accountLabel)) patch.accountLabel = res.accountLabel
    const effectiveDate = res.date || receipt.date
    if (effectiveDate) {
      const d = new Date(effectiveDate)
      if (!isNaN(d.getTime())) {
        patch.month = d.getMonth()
        if (d.getFullYear() !== receipt.year) {
          patch.year = d.getFullYear()
        }
      }
    }

    if (Object.keys(patch).length === 0) {
      // AI hittade inget nytt — beräkna quality ändå
      const merged = { ...receipt }
      patch.quality = computeQuality(merged)
      const ref = doc(db, 'users', uid, 'companies', companyId, 'receipts', receipt.id)
      await updateDoc(ref, patch)
      return { updated: false, error: 'AI hittade inget nytt att fylla i' }
    }

    // Beräkna quality baserat på merged data (original + AI-patch)
    const merged = {
      date: (patch.date as string) || receipt.date,
      title: (patch.title as string) || receipt.title,
      amount: patch.amount != null ? (patch.amount as number) : receipt.amount,
      currency: (patch.currency as string) || receipt.currency,
      vatRate: patch.vatRate != null ? (patch.vatRate as number) : receipt.vatRate,
      accountCode: patch.accountCode != null ? (patch.accountCode as number) : receipt.accountCode,
      quality: undefined as ReceiptQuality | undefined,
    }
    patch.quality = computeQuality(merged)

    const ref = doc(db, 'users', uid, 'companies', companyId, 'receipts', receipt.id)
    await updateDoc(ref, patch)
    return { updated: true }
  } catch (e) {
    // AI-fel → sätt röd quality
    try {
      const uid2 = requireUid()
      const ref = doc(db, 'users', uid2, 'companies', companyId, 'receipts', receipt.id)
      await updateDoc(ref, { quality: 'red' })
    } catch (qualityErr) { console.warn('[cloudStore] aiAnalyzeReceipt: failed to mark receipt quality=red after AI error:', qualityErr) }
    return { updated: false, error: (e as Error).message }
  }
}

// NOTE: storagePath not updated when year (or month via date) changes — known limitation (HIGH/MED from reviews).
// storagePath format includes /year/ (users/uid/company/year/cat/...) for org, but edit only patches Firestore doc.
// Consequence: re-runs via downloadUrl still work, but listing/orphans/storage ops may see stale year.
// UI warning added in EditReceiptModal (2026-06-02 subagent).
// Future: either migrate blob on year edit, or drop year from path + add storageYear field.
export async function updateReceiptFields(
  companyId: string,
  receiptId: string,
  fields: {
    date?: string; title?: string; amount?: number; currency?: string
    amountExVat?: number; vatAmount?: number; vatRate?: number
    accountCode?: number; accountLabel?: string
  }
): Promise<void> {
  if (fields.amount !== undefined && fields.amount < 0) throw new Error('Belopp kan inte vara negativt')
  if (fields.accountCode !== undefined && (fields.accountCode < 1000 || fields.accountCode > 9999)) throw new Error('Kontonummer m\u00e5ste vara 1000-9999')
  if (fields.vatRate !== undefined && ![0, 0.06, 0.12, 0.25].includes(fields.vatRate)) {
    // Allow any value 0-1 but warn for unusual rates
    if (fields.vatRate < 0 || fields.vatRate > 1) throw new Error('Momssats m\u00e5ste vara 0-1 (decimal)')
    console.warn('[cloudStore] vatRate icke-standard:', fields.vatRate, '(fr\u00e4mre 0/0.06/0.12/0.25)')
  }

  const uid = requireUid()
  const ref = doc(db, 'users', uid, 'companies', companyId, 'receipts', receiptId)
  const patch: Record<string, unknown> = {}
  if (fields.date !== undefined) {
    patch.date = fields.date || null
    const d = new Date(fields.date)
    if (!isNaN(d.getTime())) {
      patch.month = d.getMonth()
      patch.year = d.getFullYear()
    }
  }
  if (fields.title !== undefined) patch.title = fields.title || null
  if (fields.amount !== undefined) patch.amount = fields.amount
  if (fields.amountExVat !== undefined) patch.amountExVat = fields.amountExVat
  if (fields.vatAmount !== undefined) {
    patch.vatAmount = fields.vatAmount
    patch.vatStatus = 'manual'   // FAS A: manuell inmatning av moms
  }
  if (fields.vatRate !== undefined) {
    patch.vatRate = fields.vatRate
    if (patch.vatStatus == null) patch.vatStatus = 'manual'
  }
  if (fields.currency !== undefined) patch.currency = fields.currency || null
  if (fields.accountCode !== undefined) patch.accountCode = fields.accountCode
  if (fields.accountLabel !== undefined) patch.accountLabel = fields.accountLabel || null
  // Manuell redigering = verified
  if (Object.keys(patch).length > 0) {
    patch.quality = 'verified'
    await updateDoc(ref, patch)
  }
}

export async function deleteReceiptCloud(
  companyId: string,
  receipt: CloudReceipt
) {
  const uid = requireUid()
  try {
    await deleteObject(storageRef(storage, receipt.storagePath))
  } catch (e: any) {
    if (e?.code !== 'storage/object-not-found') {
      console.warn('Storage delete failed:', e)
    }
  }
  await deleteDoc(doc(db, 'users', uid, 'companies', companyId, 'receipts', receipt.id))
}

/** FAS A (2026-05-21): post räknas som "saknar moms-data" om både vatAmount
 *  och vatRate är null/undefined. Backwards-compat: gamla poster utan vatStatus
 *  behandlas också som missing om de saknar moms-fält.
 */
export function receiptIsMissingVat(r: Pick<CloudReceipt, 'vatAmount' | 'vatRate' | 'vatStatus' | 'amount'>): boolean {
  if (r.amount == null) return false  // saknar belopp helt — räknas inte
  if (r.vatStatus === 'ai_extracted' || r.vatStatus === 'manual' || r.vatStatus === 'sie_import') return false
  return r.vatAmount == null && r.vatRate == null
}

export function aggregateReceipts(receipts: CloudReceipt[]): MonthlyRow[] {
  const months = emptyMonths()
  for (const r of receipts) {
    if (r.amount == null) continue
    // Försök att härleda månad från date om month-fält saknas (säkerhetsnät för äldre poster)
    let monthIdx = r.month
    if (monthIdx == null || monthIdx < 0 || monthIdx > 11) {
      if (r.date) {
        const d = new Date(r.date)
        if (!isNaN(d.getTime())) monthIdx = d.getMonth()
      }
    }
    if (monthIdx == null || monthIdx < 0 || monthIdx > 11) continue

    // Beräkna moms och netto. Prioritet:
    //   1. Exakta värden från AI/SIE (vatAmount + amountExVat)
    //   2. Endast vatRate explicit satt → räkna ut moms ur bruttobeloppet
    //   3. Endast vatAmount explicit satt → netto = amount - vatAmount
    //   4. Saknas: vat=0, net=amount (INGEN tyst default — användaren ser
    //      detta via vatStatus='missing' och varningsbanner i MomsBalanceCard)
    //
    // HISTORIK:
    //   2026-05-20 (fd2359a): införde 25%-default på alla poster utan vatRate.
    //                         Det gav sakfel — bl.a. USD-fakturor (Anthropic, Hetzner,
    //                         Google Cloud) som har 0% svensk moms räknades som 25%.
    //                         Linespotting AB:s "moms in" landade på -103 308 kr.
    //   2026-05-21 (denna fix): TA BORT tysta defaults. Moms räknas strikt från
    //                           AI-extraherade/manuellt-ifyllda värden.
    let vat: number
    let net: number
    if (r.vatAmount != null && r.amountExVat != null) {
      vat = r.vatAmount
      net = r.amountExVat
    } else if (r.vatAmount != null) {
      vat = r.vatAmount
      net = r.amount - r.vatAmount
    } else if (r.vatRate != null) {
      vat = r.vatRate > 0 ? r.amount * r.vatRate / (1 + r.vatRate) : 0
      net = r.amount - vat
    } else {
      // INGEN moms-data → räkna inte fram moms ur bruttobeloppet.
      // Visa hela amount som netto. Användaren får varning + möjlighet
      // att köra AI-batch-analys via MomsBalanceCard / AiVatBatchDialog.
      vat = 0
      net = r.amount
    }

    if (r.category === 'kundfakturor') {
      // Intäkt → utgående moms (moms ut, betalas till SKV)
      months[monthIdx].income += net
      months[monthIdx].vatOut += vat
    } else {
      // Kostnad (leverantorsfakturor + utlagg) → ingående moms (moms in, dras av)
      months[monthIdx].expense += net
      months[monthIdx].vatIn += vat
    }
  }
  return months
}

// ============================================================================
// aggregateReceiptsByCategory (2026-05-24)
// Som aggregateReceipts men split per kategori — för per-månad-tabellerna i
// MomsBalanceCard + ProfitLossCard där användaren vill se kund/lev/utlägg
// var för sig på varje rad.
// ============================================================================
export interface CategoryMonthAgg {
  kund: { vat: number; net: number }
  lev: { vat: number; net: number }
  utl: { vat: number; net: number }
}

export function emptyCategoryMonths(): CategoryMonthAgg[] {
  return Array.from({ length: 12 }, () => ({
    kund: { vat: 0, net: 0 },
    lev: { vat: 0, net: 0 },
    utl: { vat: 0, net: 0 },
  }))
}

export function aggregateReceiptsByCategory(receipts: CloudReceipt[]): CategoryMonthAgg[] {
  const out = emptyCategoryMonths()
  for (const r of receipts) {
    if (r.amount == null) continue
    let monthIdx = r.month
    if (monthIdx == null || monthIdx < 0 || monthIdx > 11) {
      if (r.date) {
        const d = new Date(r.date)
        if (!isNaN(d.getTime())) monthIdx = d.getMonth()
      }
    }
    if (monthIdx == null || monthIdx < 0 || monthIdx > 11) continue

    let vat: number
    let net: number
    if (r.vatAmount != null && r.amountExVat != null) {
      vat = r.vatAmount; net = r.amountExVat
    } else if (r.vatAmount != null) {
      vat = r.vatAmount; net = r.amount - r.vatAmount
    } else if (r.vatRate != null) {
      vat = r.vatRate > 0 ? r.amount * r.vatRate / (1 + r.vatRate) : 0
      net = r.amount - vat
    } else {
      vat = 0; net = r.amount
    }

    const bucket =
      r.category === 'kundfakturor' ? out[monthIdx].kund :
      r.category === 'leverantorsfakturor' ? out[monthIdx].lev :
      out[monthIdx].utl
    bucket.vat += vat
    bucket.net += net
  }
  return out
}

export async function migrateLocalToCloud(
  companyId: string,
  year: number,
  localYearData: CloudYearDoc
) {
  const uid = requireUid()
  await setDoc(
    yearDocRef(uid, companyId, year),
    {
      year,
      skattekontoSaldo: localYearData.skattekontoSaldo,
      skattekontoUpdated: localYearData.skattekontoUpdated,
      villkoratTillskott: localYearData.villkoratTillskott,
      villkoratTillskottUpdated: localYearData.villkoratTillskottUpdated,
      months: localYearData.months,
    },
    { merge: true }
  )
}

export async function listAllReceipts(companyId: string, year: number) {
  const uid = requireUid()
  const q = query(receiptsCol(uid, companyId), where('year', '==', year))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }))
}

// --- Full duplicate scan ---

export interface DupScanGroup {
  reason: DuplicateMatch['reason']
  receipts: CloudReceipt[]
}

export async function scanAllDuplicates(
  companyId: string,
  year: number
): Promise<DupScanGroup[]> {
  const uid = requireUid()
  const colRef = collection(db, 'users/' + uid + '/companies/' + companyId + '/receipts')
  const q = query(colRef, where('year', '==', year))
  const snap = await getDocs(q)
  const all: CloudReceipt[] = []
  snap.forEach((d) => all.push({ id: d.id, ...d.data() } as CloudReceipt))

  const groups: DupScanGroup[] = []
  const usedInGroup = new Set<string>()

  // 1. Exact hash groups
  const byHash = new Map<string, CloudReceipt[]>()
  for (const r of all) {
    if (!r.fileHash) continue
    const arr = byHash.get(r.fileHash) ?? []
    arr.push(r)
    byHash.set(r.fileHash, arr)
  }
  for (const [, arr] of byHash) {
    if (arr.length < 2) continue
    groups.push({ reason: 'exact-hash', receipts: arr })
    arr.forEach((r) => usedInGroup.add(r.id))
  }

  // 2. Same filename groups — only if amounts match (or missing)
  const byName = new Map<string, CloudReceipt[]>()
  for (const r of all) {
    if (usedInGroup.has(r.id)) continue
    const arr = byName.get(r.filename) ?? []
    arr.push(r)
    byName.set(r.filename, arr)
  }
  for (const [, arr] of byName) {
    if (arr.length < 2) continue
    // Filter: only group if amounts are within 1 kr of each other (or missing)
    const withAmount = arr.filter((r) => r.amount != null)
    if (withAmount.length >= 2) {
      const refAmt = withAmount[0].amount!
      const allClose = withAmount.every((r) => Math.abs(r.amount! - refAmt) <= 1)
      if (!allClose) continue // Different amounts = different receipts with same filename pattern
    }
    groups.push({ reason: 'same-filename', receipts: arr })
    arr.forEach((r) => usedInGroup.add(r.id))
  }

  // 3. Fuzzy amount+date pairs — tighter thresholds, skip verified.
  // Feature 3 risk-guard: O(N) i fuzzy-loopen kan bli langsam vid 500+
  // kvitton. Skippa fuzzy-passet helt om totala antalet kvitton verstiger
  // grnsvrdet — hash + namn-passen tcker fortfarande exakta dubletter.
  const FUZZY_SKIP_LIMIT = 500
  if (all.length > FUZZY_SKIP_LIMIT) {
    return groups
  }
  const remaining = all.filter((r) => !usedInGroup.has(r.id))
  for (let i = 0; i < remaining.length; i++) {
    const a = remaining[i]
    if (usedInGroup.has(a.id) || a.amount == null || !a.date) continue
    if (a.quality === 'verified') continue // Verified = confirmed unique by user
    const cluster = [a]
    for (let j = i + 1; j < remaining.length; j++) {
      const b = remaining[j]
      if (usedInGroup.has(b.id) || b.amount == null || !b.date) continue
      if (b.quality === 'verified') continue
      const amountClose = Math.abs(a.amount! - b.amount!) <= 1 // Within 1 kr (was 5)
      const d1 = new Date(a.date!).getTime()
      const d2 = new Date(b.date!).getTime()
      const dateClose = !isNaN(d1) && !isNaN(d2) && Math.abs(d1 - d2) <= 86400000 // 1 day (was 2)
      if (amountClose && dateClose) {
        cluster.push(b)
        usedInGroup.add(b.id)
      }
    }
    if (cluster.length >= 2) {
      groups.push({ reason: 'fuzzy-amount-date', receipts: cluster })
      cluster.forEach((r) => usedInGroup.add(r.id))
    }
  }

  return groups
}

export async function saveDupScanTimestamp(
  companyId: string,
  year: number
) {
  const uid = requireUid()
  await setDoc(
    yearDocRef(uid, companyId, year),
    { dupScanDate: new Date().toISOString() },
    { merge: true }
  )
}

// --- Sync-status per kategori (Feature 1: "Senast syncad") ---

/**
 * Spara senast-syncad-status fr en kategori pa years/{year}-doc:et.
 * Anropas efter en upload-batch fran processUploadQueue (manuell upload),
 * dropbox-scanner-importen och SIE-importen.
 */
export async function recordSync(
  companyId: string,
  year: number,
  category: ReceiptCategory,
  fileCount: number,
  source: CategorySyncStatus['source'] = 'manual-upload',
): Promise<void> {
  if (fileCount <= 0) return
  const uid = requireUid()
  const ref = yearDocRef(uid, companyId, year)
  await setDoc(ref, {
    year,
    syncStatus: {
      [category]: {
        lastSyncAt: new Date().toISOString(),
        fileCount,
        source,
      },
    },
  }, { merge: true })
}

// --- Dashboard prefs (card order + collapsed state) ---

export interface DashboardPrefs {
  /** Card order per company: { [companyId]: ['skatte','pl','moms','kvitton'] } */
  cardOrder?: Record<string, string[]>
  /** Collapsed state per company: { [companyId]: { skatte: false, pl: true, ... } } */
  collapsed?: Record<string, Record<string, boolean>>
}

function prefsDocRef(uid: string) {
  return doc(db, 'users', uid, 'prefs', 'dashboard')
}

export function subscribeDashboardPrefs(
  cb: (prefs: DashboardPrefs) => void
): () => void {
  const uid = requireUid()
  return onSnapshot(prefsDocRef(uid), (snap) => {
    const data = snap.data()
    cb(data ? (data as DashboardPrefs) : {})
  })
}

export async function saveDashboardPrefs(prefs: DashboardPrefs) {
  const uid = requireUid()
  await setDoc(prefsDocRef(uid), prefs, { merge: true })
}
